import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Remote, Rpc } from 'accounts'
import { useEffect, useMemo } from 'react'
import { useConnection } from 'wagmi'
import * as z from 'zod/mini'

import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { remote, wagmiConfig } from '#/lib/config.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import * as TransactionFrames from '../-frames/transaction/index.js'
import { usePrepareAlert } from '../-hooks/usePrepareAlert.js'
import { useUpdateAccessKeyTransaction } from '../-hooks/useUpdateAccessKeyTransaction.js'
import * as AccessKeyLimits from '../-lib/AccessKeyLimits.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'

export const Route = createFileRoute('/remote/rpc/wallet_updateAccessKey')({
  component: Component,
  validateSearch: (search) =>
    RemoteRpc.validateSearch(search, { method: 'wallet_updateAccessKey' }),
})

type Search = Remote.validateSearch.ReturnType<'wallet_updateAccessKey'>

function Component() {
  const { capture } = useAnalytics()
  const queryClient = useQueryClient()
  const search = Route.useSearch() as Search
  const rpc = RemoteRpc.use()
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  const params = search._decoded.params[0]
  const { address, chainId } = useConnection()

  const resolvedChainId = Number(params.chainId ?? chainId ?? wagmiConfig.state.chainId)
  const apps = AuthorizedApps.useAuthorizedApps({
    account: params.address,
    chainId: resolvedChainId,
  })
  const stored = useMemo(() => {
    const address = params.accessKeyAddress.toLowerCase()
    for (const app of apps.data ?? [])
      for (const key of app.keys) if (key.address.toLowerCase() === address) return key
    return undefined
  }, [apps.data, params.accessKeyAddress])
  const pending = stored?.state === 'pending' ? stored : undefined
  const currentLimits = useMemo(
    () => AccessKeyLimits.getCurrent(apps.data, params.accessKeyAddress),
    [apps.data, params.accessKeyAddress],
  )
  const remainingLimitError = currentLimits === null

  useEffect(() => {
    capture('access_key_update_started')
  }, [capture])

  const transaction = useUpdateAccessKeyTransaction({
    accessKeyAddress: params.accessKeyAddress,
    address,
    chainId: resolvedChainId,
    enabled: !pending,
    limits: params.limits,
  })
  const { autoSwap, capabilities, funds, prepare } = transaction

  const confirm = useMutation({
    mutationFn: async () => {
      if (remainingLimitError) throw new Error('Unable to load the current spending limit.')
      if (pending) {
        const parameters = Rpc.wallet_authorizeAccessKey.parameters.parse({
          address: params.accessKeyAddress,
          chainId: BigInt(resolvedChainId),
          expiry: pending.expiry,
          keyType: pending.keyType,
          limits: params.limits.map((limit) => {
            const current = pending.limits.find(
              (candidate) => candidate.token.toLowerCase() === limit.token.toLowerCase(),
            )
            return {
              ...limit,
              ...(current?.period != null ? { period: current.period } : {}),
            }
          }),
          ...(pending.scopes ? { scopes: pending.scopes } : {}),
        })
        const result = await remote.provider.request({
          method: 'wallet_authorizeAccessKey',
          params: [z.encode(Rpc.wallet_authorizeAccessKey.parameters, parameters)],
        })
        await AuthorizedApps.upsert.strict({
          chainId: resolvedChainId,
          hostname: consumer.host,
          key: parameters,
          updateExisting: true,
        })
        await queryClient.invalidateQueries({
          queryKey: AuthorizedApps.queryKey(params.address, { chainId: resolvedChainId }),
        })
        return rpc.resolve({ keyAuthorization: result.keyAuthorization })
      }
      await transaction.send()
      return rpc.resolve(undefined)
    },
    onSuccess: () => {
      capture('access_key_update_success')
      queryClient.invalidateQueries({
        queryKey: AuthorizedApps.queryKey(params.address, { chainId: resolvedChainId }),
      })
    },
    onError: (error) => capture('access_key_update_failed', { error: error.message }),
  })

  const prepareAlert = usePrepareAlert({
    mutation: confirm,
    query: prepare,
    suppress: !!pending,
  })
  const alert = (() => {
    if (remainingLimitError)
      return { message: 'Unable to load the current spending limit.', type: 'error' as const }
    if (pending && confirm.error) return { message: confirm.error.message, type: 'error' as const }
    return prepareAlert
  })()

  if (!pending && funds.frame) return funds.frame

  return (
    <TransactionFrames.UpdateAccessKey
      actions={pending || remainingLimitError ? undefined : funds.actions}
      alert={alert}
      autoSwap={autoSwap}
      chainId={resolvedChainId}
      confirming={confirm.isPending}
      currentLimits={currentLimits ?? undefined}
      disabled={remainingLimitError}
      fee={pending ? undefined : capabilities?.fee}
      host={host}
      limits={params.limits}
      loading={apps.isLoading || (!pending && prepare.isLoading)}
      onConfirm={() => confirm.mutate()}
      onReject={() => rpc.reject()}
      onRetry={() => {
        if (remainingLimitError) return void apps.refetch()
        if (pending && confirm.error) return confirm.mutate()
        void prepare.refetch()
      }}
      sponsor={pending ? undefined : capabilities?.sponsor}
    />
  )
}
